import { Injectable, inject } from '@angular/core';
import { map, Observable } from 'rxjs';

import { ApiClientService } from '../../../core/http/api-client.service';
import {
  ClientAdminDetailDto,
  ClientAdminDetailItem,
  ClientAdminDto,
  ClientAdminPageDto,
  ClientAdminPageItem,
  ClientAdminItem,
  ClientCommunicationChannels,
  ClientCommunicationChannelsDto,
  CreateClientRequest,
  CreateClientIntakeRequest,
  SendClientInviteRequest,
  SendClientInviteResponse,
  UpdateClientRequest,
  UpdateClientContactRequest,
} from './clients-admin.models';
import type { UserClientRatingEventDto } from '../../users/data-access/users.models';

@Injectable({
  providedIn: 'root'
})
export class ClientsAdminService {
  private readonly apiClient = inject(ApiClientService);

  public getList(companyId?: string): Observable<ClientAdminItem[]> {
    return this.apiClient.get<ClientAdminDto[] | null>(
      '/clients',
      this.scopedQuery(companyId),
    ).pipe(
      map((items) => Array.isArray(items) ? items.map((dto) => this.mapClient(dto)) : [])
    );
  }

  public getPage(
    params: {
      companyId?: string;
      limit: number;
      cursor?: string | null;
      query?: string;
      accountStatus?: 'no_account' | 'invited' | 'registered' | '';
      emailPresence?: 'with_email' | 'without_email' | '';
      origin?: 'self_registration' | 'invite' | 'offline_only' | '';
      sort?: 'createdAtDesc' | 'createdAtAsc';
    },
  ): Observable<ClientAdminPageItem> {
    return this.apiClient.get<ClientAdminPageDto>(
      '/clients',
      {
        ...this.scopedQuery(params.companyId),
        limit: params.limit,
        cursor: params.cursor ?? undefined,
        q: params.query?.trim() || undefined,
        accountStatus: params.accountStatus || undefined,
        emailPresence: params.emailPresence || undefined,
        origin: params.origin || undefined,
        sort: params.sort ?? 'createdAtDesc',
      },
    ).pipe(
      map((dto) => ({
        items: Array.isArray(dto?.items) ? dto.items.map((item) => this.mapClient(item)) : [],
        nextCursor: dto?.nextCursor ?? null,
        hasMore: !!dto?.hasMore,
      })),
    );
  }

  public getById(clientId: string, companyId?: string): Observable<ClientAdminItem> {
    return this.apiClient.get<ClientAdminDto>(
      `/clients/${encodeURIComponent(clientId)}`,
      this.scopedQuery(companyId),
    ).pipe(
      map((dto) => this.mapClient(dto))
    );
  }

  public getDetailById(clientId: string, companyId?: string): Observable<ClientAdminDetailItem> {
    return this.apiClient.get<ClientAdminDetailDto>(
      `/clients/${encodeURIComponent(clientId)}/detail`,
      this.scopedQuery(companyId),
    ).pipe(
      map((dto) => ({
        ...this.mapClient(dto),
        ratingEvents: this.mapRatingEvents(dto.ratingEvents ?? []),
      })),
    );
  }

  public createIntake(payload: CreateClientIntakeRequest, companyId?: string): Observable<ClientAdminItem> {
    return this.apiClient.post<ClientAdminDto, CreateClientIntakeRequest>(
      '/clients/intake',
      payload,
      this.scopedQuery(companyId),
    ).pipe(
      map((dto) => this.mapClient(dto))
    );
  }

  public create(payload: CreateClientRequest, companyId?: string): Observable<ClientAdminItem> {
    return this.apiClient.post<ClientAdminDto, CreateClientRequest>(
      '/clients',
      payload,
      this.scopedQuery(companyId),
    ).pipe(
      map((dto) => this.mapClient(dto))
    );
  }

  public updateContact(
    clientId: string,
    payload: UpdateClientContactRequest,
    companyId?: string,
  ): Observable<ClientAdminItem> {
    return this.apiClient.patch<ClientAdminDto, UpdateClientContactRequest>(
      `/clients/${encodeURIComponent(clientId)}/contact`,
      payload,
      this.scopedQuery(companyId),
    ).pipe(
      map((dto) => this.mapClient(dto))
    );
  }

  public update(
    clientId: string,
    payload: UpdateClientRequest,
    companyId?: string,
  ): Observable<ClientAdminItem> {
    return this.apiClient.patch<ClientAdminDto, UpdateClientRequest>(
      `/clients/${encodeURIComponent(clientId)}`,
      payload,
      this.scopedQuery(companyId),
    ).pipe(
      map((dto) => this.mapClient(dto))
    );
  }

  public delete(clientId: string, companyId?: string): Observable<{ deleted: true }> {
    return this.apiClient.delete<{ deleted: true }>(
      `/clients/${encodeURIComponent(clientId)}`,
      this.scopedQuery(companyId),
    );
  }

  public sendInvite(
    clientId: string,
    payload: SendClientInviteRequest,
    companyId?: string,
  ): Observable<SendClientInviteResponse> {
    return this.apiClient.post<SendClientInviteResponse, SendClientInviteRequest>(
      `/clients/${encodeURIComponent(clientId)}/invites`,
      payload,
      this.scopedQuery(companyId),
    );
  }

  private mapClient(dto: ClientAdminDto): ClientAdminItem {
    return {
      id: dto.id,
      companyId: dto.companyId,
      userId: dto.userId,
      photoUrl: dto.photoUrl ?? null,
      fullName: dto.fullName,
      ratingPercent: dto.ratingPercent ?? 100,
      phone: dto.phone,
      email: dto.email,
      birthDate: dto.birthDate,
      notes: dto.notes,
      accountOrigin: dto.accountOrigin,
      accountOriginSetAt: dto.accountOriginSetAt,
      accountStatus: dto.accountStatus,
      inviteExpiresAt: dto.inviteExpiresAt,
      communicationChannels: this.mapCommunicationChannels(dto.communicationChannels ?? null, dto),
      createdAt: dto.createdAt,
      updatedAt: dto.updatedAt,
    };
  }

  private mapRatingEvents(items: UserClientRatingEventDto[] | null): ClientAdminDetailItem['ratingEvents'] {
    if (!Array.isArray(items)) {
      return [];
    }

    return items.map((item) => ({
      id: item.id,
      clientId: item.clientId,
      appointmentId: item.appointmentId ?? null,
      eventType: item.eventType,
      sourceType: item.sourceType,
      sourceId: item.sourceId,
      deltaPercent: item.deltaPercent,
      scoreBefore: item.scoreBefore,
      scoreAfter: item.scoreAfter,
      actorUserId: item.actorUserId ?? null,
      actorRole: item.actorRole ?? null,
      note: item.note ?? null,
      payloadJson: item.payloadJson ?? null,
      createdAt: item.createdAt,
    }));
  }

  private mapCommunicationChannels(
    dto: ClientCommunicationChannelsDto | null,
    client?: Pick<ClientAdminDto, 'email' | 'phone'>,
  ): ClientCommunicationChannels {
    if (dto) {
      return dto;
    }

    const emailAvailable = !!client?.email?.trim();
    const phoneAvailable = !!client?.phone?.trim();
    const automaticChannels = emailAvailable ? (['email'] as const) : [];
    const manualChannels = phoneAvailable ? (['phone'] as const) : [];

    return {
      email: {
        status: emailAvailable ? 'available' : 'missing',
        deliveryMode: emailAvailable ? 'automatic' : 'none',
      },
      phone: {
        status: phoneAvailable ? 'manual' : 'missing',
        deliveryMode: phoneAvailable ? 'manual' : 'none',
      },
      telegram: {
        status: 'unlinked',
        deliveryMode: 'none',
        username: null,
        linkedAt: null,
        revokedAt: null,
        blockedAt: null,
        lastDeliveryFailedAt: null,
        lastDeliveryError: null,
      },
      summary: {
        automaticReachable: automaticChannels.length > 0,
        manualReachable: manualChannels.length > 0,
        manualFollowUpRequired: automaticChannels.length === 0 && manualChannels.length > 0,
        reachabilityStatus: automaticChannels.length > 0
          ? 'automatic'
          : manualChannels.length > 0
            ? 'manual'
            : 'unreachable',
        automaticChannels: [...automaticChannels],
        manualChannels: [...manualChannels],
      },
    };
  }

  private scopedQuery(companyId?: string): Record<string, string> | undefined {
    const normalizedCompanyId = companyId?.trim();
    if (!normalizedCompanyId) {
      return undefined;
    }

    return { companyId: normalizedCompanyId };
  }
}
